import type { Domain, TerrainType, UnitType } from './types';
import { resolveBaseType } from './units';

export interface TerrainDef {
  type: TerrainType;
  label: string;
  movementCost: number; // Infinity = impassable for that check
  defenseBonus: number; // multiplier on defender's defense stat
  visionCost: number; // how much sight budget looking through this tile costs
  passableBy: Set<Domain>;
  color: number; // hex color for rendering
}

export const TERRAIN_DEFS: Record<TerrainType, TerrainDef> = {
  plains: {
    type: 'plains',
    label: 'plains',
    movementCost: 1,
    defenseBonus: 1.0,
    visionCost: 1,
    passableBy: new Set<Domain>(['land', 'air']),
    color: 0x8fbc5e,
  },
  forest: {
    type: 'forest',
    label: 'forest',
    movementCost: 2,
    defenseBonus: 1.5,
    visionCost: 2,
    passableBy: new Set<Domain>(['land', 'air']),
    color: 0x3d7a2a,
  },
  mountain: {
    type: 'mountain',
    label: 'mountain',
    movementCost: 3, // infantry only (tanks can't pass)
    defenseBonus: 2.0,
    visionCost: 3,
    passableBy: new Set<Domain>(['air']), // only air can fly over; infantry handled specially
    color: 0x8b7355,
  },
  road: {
    type: 'road',
    label: 'road',
    movementCost: 0.5,
    defenseBonus: 0.8,
    visionCost: 1,
    passableBy: new Set<Domain>(['land', 'air']),
    color: 0xc4a95a,
  },
  water: {
    type: 'water',
    label: 'water',
    movementCost: 1,
    defenseBonus: 1.0,
    visionCost: 1,
    passableBy: new Set<Domain>(['water', 'air']),
    color: 0x4a90d9,
  },
  bridge: {
    type: 'bridge',
    label: 'bridge',
    movementCost: 0.5,
    defenseBonus: 0.8,
    visionCost: 1,
    passableBy: new Set<Domain>(['land', 'water', 'air']),
    color: 0x4a90d9,
  },
  /**
   * Coast = amphibious terrain that both land and water units can enter.
   * Use for shorelines, shallow lagoons, beaches — anywhere ships and
   * infantry should be able to coexist. Costlier than plains/water so
   * units don't prefer it as a shortcut.
   */
  coast: {
    type: 'coast',
    label: 'coast',
    movementCost: 2,
    defenseBonus: 0.9,
    visionCost: 1,
    passableBy: new Set<Domain>(['land', 'water', 'air']),
    color: 0x6fa8c0,
  },
};

/**
 * Base terrains are "empty" tiles whose properties are not overridden by a
 * terrain object. Buildings may only be placed on base terrains — object
 * terrains (forest, mountain, road, bridge) and buildings/bunkers are the
 * things that override a tile's defense/movement.
 */
export const BASE_TERRAINS = new Set<TerrainType>(['plains', 'water', 'coast']);

/**
 * Base LAND terrains on which buildings may be placed. Water is a base terrain
 * but not buildable for a land structure; object terrains never qualify.
 */
export const BUILDABLE_BUILDING_TERRAINS = new Set<TerrainType>(['plains', 'coast']);

/** True if `t` is a base (buildable) terrain. */
export function isBaseTerrain(t: TerrainType): boolean {
  return BASE_TERRAINS.has(t);
}

/** True if `t` is an object terrain that overrides the base tile's properties. */
export function isTerrainObject(t: TerrainType): boolean {
  return !BASE_TERRAINS.has(t);
}

/** Check if a domain can enter a terrain type. Infantry can enter mountains (slow). */
export function canTraverse(terrain: TerrainType, domain: Domain, unitType?: string): boolean {
  const def = TERRAIN_DEFS[terrain];
  if (def.passableBy.has(domain)) return true;
  // Special: infantry and recon can traverse mountains (other land units cannot)
  if (terrain === 'mountain' && domain === 'land' && unitType) {
    const base = resolveBaseType(unitType as UnitType);
    if (base === 'infantry' || base === 'infantry-bazooka' || base === 'recon') return true;
  }
  return false;
}

/** Get vision cost for looking through a terrain tile. Air domain always costs 1. */
export function getVisionCost(terrain: TerrainType, domain: Domain): number {
  if (domain === 'air') return 1;
  return TERRAIN_DEFS[terrain].visionCost;
}

/** Get movement cost for a unit type on terrain. Returns Infinity if impassable. */
export function getMovementCost(terrain: TerrainType, domain: Domain, unitType?: string): number {
  if (!canTraverse(terrain, domain, unitType)) return Infinity;
  if (domain === 'air') return 1; // aircraft pay flat 1 everywhere
  return TERRAIN_DEFS[terrain].movementCost;
}

/**
 * Coast tile = buildable land tile (plains/forest/road) with at least
 * one cardinal water neighbor. Ports may only be placed on coast tiles.
 * Water, bridge, and mountain tiles never qualify as coast.
 * `getTerrain` returns the terrain type at (c, r), or undefined out of bounds.
 */
export function isCoast(
  getTerrain: (c: number, r: number) => TerrainType | undefined,
  col: number,
  row: number,
): boolean {
  const t = getTerrain(col, row);
  if (!t || t === 'water' || t === 'bridge' || t === 'mountain') return false;
  return (
    getTerrain(col, row - 1) === 'water' ||
    getTerrain(col, row + 1) === 'water' ||
    getTerrain(col - 1, row) === 'water' ||
    getTerrain(col + 1, row) === 'water'
  );
}
